import React, { useEffect, useState } from "react";
import axiosInstance from "../api/axiosConfig";

import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip,
  Circle,
  LayerGroup,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import customMarkerIcon from "../assets/0emwie0m.png";
import customMarkerShadow from "leaflet/dist/images/marker-shadow.png";

// Chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ChartDataLabels
);

// Paleta personalizada para cada especialidad (adaptada al ERP Renault)
const colorMap = {
  MECANICA: "#004785", // Azul profundo
  COLISIONES: "#E60012", // Rojo Renault
  SIN_REGISTRO: "#9B9B9B", // Gris
};

// Función para obtener el color de concentración con tonos cálidos
const getConcentrationColor = (total, maxTotal) => {
  const ratio = total / maxTotal;
  if (ratio < 0.2) return "#FFF9C4"; // Amarillo muy claro
  if (ratio < 0.4) return "#FFECB3"; // Amarillo claro
  if (ratio < 0.6) return "#FFC107"; // Ámbar
  if (ratio < 0.8) return "#FF9800"; // Naranja
  return "#F57C00"; // Naranja oscuro
};

// Función para obtener la opacidad basada en la concentración
const getConcentrationOpacity = (total, maxTotal) => {
  const ratio = total / maxTotal;
  return 0.3 + ratio * 0.5;
};

// Estilo para el tooltip (sin fondo ni borde)
const tooltipStyle = {
  background: "transparent",
  border: "none",
  boxShadow: "none",
  color: "#ccc",
  fontWeight: "bold",
  fontSize: "14px",
};

//
// Componente interactivo para la vista "Marcadores"
// (con círculo de fondo, icono dinámico y tooltip con el total)
//
const InteractiveMarker = ({ b, maxConcentration }) => {
  const [hovered, setHovered] = useState(false);
  const map = useMap();
  const [currentZoom, setCurrentZoom] = useState(map.getZoom());

  useEffect(() => {
    const handleZoom = () => setCurrentZoom(map.getZoom());
    map.on("zoomend", handleZoom);
    return () => {
      map.off("zoomend", handleZoom);
    };
  }, [map]);

  const zoomFactor = currentZoom / 7;

  const dynamicIcon = L.icon({
    iconUrl: customMarkerIcon,
    shadowUrl: customMarkerShadow,
    iconSize: [35 * zoomFactor, 45 * zoomFactor],
    iconAnchor: [17 * zoomFactor, 45 * zoomFactor],
    popupAnchor: [0, -40 * zoomFactor],
  });

  const baseRadius = Math.max(2000, (b.total / maxConcentration) * 2000);
  const circleRadius = (hovered ? baseRadius * 1.5 : baseRadius) * zoomFactor;
  const opacity = hovered ? 0.7 : 0.5;

  return (
    <>
      <Circle
        center={[b.latitud, b.longitud]}
        radius={circleRadius}
        fillColor={getConcentrationColor(b.total, maxConcentration)}
        fillOpacity={opacity}
        stroke={false}
        eventHandlers={{
          mouseover: () => setHovered(true),
          mouseout: () => setHovered(false),
        }}
      />
      <Marker
        position={[b.latitud, b.longitud]}
        icon={dynamicIcon}
        eventHandlers={{
          mouseover: () => setHovered(true),
          mouseout: () => setHovered(false),
        }}
      >
        <Tooltip permanent direction="top" offset={[0, -30]} opacity={1}>
          <div style={tooltipStyle}>{b.total}</div>
        </Tooltip>
      </Marker>
    </>
  );
};

//
// Nuevo componente para mostrar el marcador (icono dinámico)
// en la vista "Gradiente de Concentración".
//
const GradientMarker = ({ b, maxConcentration }) => {
  const map = useMap();
  const currentZoom = map.getZoom();
  const zoomFactor = currentZoom / 7;
  const dynamicIcon = L.icon({
    iconUrl: customMarkerIcon,
    shadowUrl: customMarkerShadow,
    iconSize: [35 * zoomFactor, 45 * zoomFactor],
    iconAnchor: [17 * zoomFactor, 45 * zoomFactor],
    popupAnchor: [0, -40 * zoomFactor],
  });
  return (
    <Marker position={[b.latitud, b.longitud]} icon={dynamicIcon}>
      <Tooltip permanent direction="top" offset={[0, -30]} opacity={1}>
        <div style={tooltipStyle}>{b.total}</div>
      </Tooltip>
    </Marker>
  );
};

//
// Componente principal del dashboard
//
const MapConcentrationDashboard = () => {
  const [reports, setReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState("");
  const [bodegasData, setBodegasData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [simActivaSummary, setSimActivaSummary] = useState(null);
  const [viewMode, setViewMode] = useState("markers");
  const [maxConcentration, setMaxConcentration] = useState(0);
  const [selectedCity, setSelectedCity] = useState("");

  // Cargar lista de reportes
  useEffect(() => {
    axiosInstance
      .get("/reports")
      .then((res) => setReports(res.data))
      .catch((err) => console.error("Error al obtener reportes:", err));
  }, []);

  // Cargar datos de concentración al seleccionar un reporte
  useEffect(() => {
    if (!selectedReportId) {
      setBodegasData([]);
      setSummary(null);
      setSimActivaSummary(null);
      return;
    }

    axiosInstance
      .get(`/reports/${selectedReportId}/map-concentration`)
      .then((res) => {
        setBodegasData(res.data);
        const max = Math.max(...res.data.map((b) => b.total));
        setMaxConcentration(max);
      })
      .catch((err) => console.error("Error al obtener concentración:", err));

    axiosInstance
      .get(`/reports/${selectedReportId}/concentration-summary`)
      .then((res) => setSummary(res.data))
      .catch((err) =>
        console.error("Error al obtener resumen de concentración:", err)
      );

    axiosInstance
      .get(`/reports/${selectedReportId}/sim-activa-summary`)
      .then((res) => setSimActivaSummary(res.data))
      .catch((err) =>
        console.error("Error al obtener sim_activa summary:", err)
      );
  }, [selectedReportId]);

  const centerEcuador = [-1.8312, -78.1834];

  // Generar lista de ciudades (verifica que la columna "ciudad" exista en tus datos)
  const cities = [
    ...new Set(bodegasData.map((b) => b.ciudad).filter((city) => city)),
  ];

  // Filtrar datos según la ciudad seleccionada
  const filteredBodegasData = selectedCity
    ? bodegasData.filter((b) => b.ciudad === selectedCity)
    : bodegasData;

  // Datos para el gráfico de barras
  const chartLabels = filteredBodegasData.map((b) => b.bodegaNombre);
  const chartValues = filteredBodegasData.map((b) => b.total);
  const barColors = filteredBodegasData.map((b) =>
    getConcentrationColor(b.total, maxConcentration)
  );

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Concentración de Vehículos",
        data: chartValues,
        backgroundColor: barColors,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    color: "#ccc",
    plugins: {
      legend: {
        position: "bottom",
        labels: { color: "#ccc" },
      },
      title: {
        display: true,
        text: "Concentración por Bodega",
        font: { size: 16 },
        color: "#ccc",
      },
      datalabels: {
        display: true,
        anchor: "end",
        align: "end",
        color: "#ccc",
        font: { size: 12, weight: "bold" },
        formatter: (value) => value,
      },
      tooltip: {
        titleColor: "#ccc",
        bodyColor: "#ccc",
        backgroundColor: "#333",
        borderColor: "#666",
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        ticks: { color: "#ccc" },
        title: {
          display: true,
          text: "Bodega",
          color: "#ccc",
          font: { size: 14 },
        },
      },
      y: {
        beginAtZero: true,
        ticks: { color: "#ccc" },
        title: {
          display: true,
          text: "Total de Vehículos",
          color: "#ccc",
          font: { size: 14 },
        },
      },
    },
  };

  // Leyenda de colores para el mapa
  const renderColorLegend = () => {
    const steps = 5;
    const legendItems = [];
    for (let i = 0; i < steps; i++) {
      const ratio = i / (steps - 1);
      const threshold = Math.round(maxConcentration * ratio);
      const color = getConcentrationColor(threshold, maxConcentration);
      legendItems.push(
        <div
          key={i}
          style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}
        >
          <div
            style={{
              width: "20px",
              height: "20px",
              backgroundColor: color,
              marginRight: "8px",
              border: "1px solid #555",
            }}
          />
          <span style={{ fontSize: "12px" }}>
            {i === 0
              ? `0-${Math.round(maxConcentration * 0.2)}`
              : i === steps - 1
              ? `${Math.round(maxConcentration * 0.8)}+`
              : `${Math.round((maxConcentration * (i - 1)) / 5)}-${Math.round(
                  (maxConcentration * i) / 5
                )}`}
          </span>
        </div>
      );
    }
    return (
      <div
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          background: "rgba(47, 47, 47, 0.8)",
          padding: "10px",
          borderRadius: "5px",
          border: "1px solid #555",
          zIndex: 1000,
        }}
      >
        <div
          style={{ marginBottom: "5px", fontWeight: "bold", fontSize: "12px" }}
        >
          Concentración
        </div>
        {legendItems}
      </div>
    );
  };

  return (
    <div
      style={{
        width: "100%",
        background: "#1f1f1f",
        color: "#ccc",
        padding: "1rem",
        borderRadius: "8px",
        boxShadow: "0 0 10px rgba(0,0,0,0.5)",
        fontFamily: "'Segoe UI', Tahoma, sans-serif",
      }}
    >
      {/* Título y selectores */}
      <div style={{ marginBottom: "1rem" }}>
        <h3 style={{ margin: 0, color: "#fff" }}>Mapa de Concentración</h3>
        <div
          style={{
            marginTop: "0.5rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontWeight: 600, fontSize: "14px", color: "#ccc" }}>
            Seleccionar Reporte:
          </span>
          <select
            value={selectedReportId}
            onChange={(e) => {
              setSelectedReportId(e.target.value);
              setSelectedCity("");
            }}
            style={{
              padding: "0.4rem",
              fontSize: "14px",
              borderRadius: "4px",
              minWidth: "200px",
              border: "1px solid #444",
              background: "#2a2a2a",
              color: "#ccc",
            }}
          >
            <option value="">-- Seleccionar --</option>
            {reports.map((r) => (
              <option key={r.id} value={r.id}>
                {r.id} - {r.month}/{r.year}
              </option>
            ))}
          </select>

          {/* Selector de filtro por ciudad */}
          <span style={{ fontWeight: 600, fontSize: "14px", color: "#ccc" }}>
            Ciudad:
          </span>
          <select
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            style={{
              padding: "0.4rem",
              fontSize: "14px",
              borderRadius: "4px",
              minWidth: "150px",
              border: "1px solid #444",
              background: "#2a2a2a",
              color: "#ccc",
            }}
          >
            <option value="">Todas</option>
            {cities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>

          {/* Selector de modo de visualización */}
          <span style={{ fontWeight: 600, fontSize: "14px", color: "#ccc" }}>
            Visualización:
          </span>
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            style={{
              padding: "0.4rem",
              fontSize: "14px",
              borderRadius: "4px",
              minWidth: "150px",
              border: "1px solid #444",
              background: "#2a2a2a",
              color: "#ccc",
            }}
          >
            <option value="markers">Marcadores</option>
            <option value="circles">Círculos de Concentración</option>
            <option value="gradient">Gradiente de Concentración</option>
          </select>
        </div>
      </div>

      {/* Layout en dos columnas: Mapa y Gráfico */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
        {/* Mapa */}
        <div
          style={{
            flex: "1 1 400px",
            height: "600px",
            border: "1px solid #333",
            borderRadius: "8px",
            overflow: "hidden",
            minWidth: "350px",
            position: "relative",
          }}
        >
          <MapContainer
            center={centerEcuador}
            zoom={7}
            style={{ width: "100%", height: "100%" }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            {viewMode === "markers" &&
              filteredBodegasData.map((b) => (
                <InteractiveMarker
                  key={b.bodegaCodigo}
                  b={b}
                  maxConcentration={maxConcentration}
                />
              ))}

            {viewMode === "circles" &&
              filteredBodegasData.map((b) => (
                <Circle
                  key={b.bodegaCodigo}
                  center={[b.latitud, b.longitud]}
                  radius={Math.max(1000, (b.total / maxConcentration) * 30000)}
                  fillColor={getConcentrationColor(b.total, maxConcentration)}
                  fillOpacity={getConcentrationOpacity(
                    b.total,
                    maxConcentration
                  )}
                  stroke={true}
                  color="#fff"
                  weight={1}
                >
                  <Tooltip
                    permanent
                    direction="top"
                    offset={[0, -30]}
                    opacity={1}
                  >
                    <div style={tooltipStyle}>{b.total}</div>
                  </Tooltip>
                </Circle>
              ))}

            {viewMode === "gradient" && (
              <LayerGroup>
                {/* Círculos grandes para efecto de gradiente */}
                {filteredBodegasData.map((b) => (
                  <Circle
                    key={`large-${b.bodegaCodigo}`}
                    center={[b.latitud, b.longitud]}
                    radius={Math.max(
                      5000,
                      (b.total / maxConcentration) * 50000
                    )}
                    fillColor={getConcentrationColor(b.total, maxConcentration)}
                    fillOpacity={0.2}
                    stroke={false}
                  />
                ))}
                {/* Círculos intermedios */}
                {filteredBodegasData.map((b) => (
                  <Circle
                    key={`medium-${b.bodegaCodigo}`}
                    center={[b.latitud, b.longitud]}
                    radius={Math.max(
                      2500,
                      (b.total / maxConcentration) * 25000
                    )}
                    fillColor={getConcentrationColor(b.total, maxConcentration)}
                    fillOpacity={0.3}
                    stroke={false}
                  />
                ))}
                {/* Marcadores con icono dinámico para gradiente */}
                {filteredBodegasData.map((b) => (
                  <React.Fragment key={`marker-group-${b.bodegaCodigo}`}>
                    <Circle
                      center={[b.latitud, b.longitud]}
                      radius={Math.max(
                        500,
                        (b.total / maxConcentration) * 10000
                      )}
                      fillColor={getConcentrationColor(
                        b.total,
                        maxConcentration
                      )}
                      fillOpacity={0.5}
                      color="#fff"
                      weight={1}
                    />
                    <GradientMarker b={b} maxConcentration={maxConcentration} />
                  </React.Fragment>
                ))}
              </LayerGroup>
            )}
          </MapContainer>

          {bodegasData.length > 0 && renderColorLegend()}
        </div>

        {/* Gráfico de barras */}
        <div
          style={{
            flex: "1 1 400px",
            minHeight: "600px",
            border: "1px solid #333",
            borderRadius: "8px",
            padding: "1rem",
            background: "#2a2a2a",
            minWidth: "350px",
          }}
        >
          <div style={{ width: "100%", height: "100%" }}>
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>
      </div>

      {/* Resumen de chasis */}
      {summary && (
        <div
          style={{
            marginTop: "1rem",
            background: "#2a2a2a",
            color: "#ccc",
            padding: "1.5rem",
            borderRadius: "8px",
            boxShadow: "0 0 5px rgba(0,0,0,0.5)",
            border: "1px solid #333",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "1rem",
            }}
          >
            <div
              style={{
                background: "#1f1f1f",
                borderRadius: "8px",
                padding: "1rem",
                textAlign: "center",
                border: "1px solid #444",
              }}
            >
              <div style={{ fontSize: "14px", color: "#888" }}>
                TALLER AUTORIZADO
              </div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  marginTop: "0.5rem",
                }}
              >
                {summary.totalChasis}
              </div>
            </div>
            {summary.data.map((item) => {
              const color = colorMap[item.especialidad] || "#8c8c8c";
              return (
                <div
                  key={item.especialidad}
                  style={{
                    background: "#1f1f1f",
                    borderRadius: "8px",
                    padding: "1rem",
                    border: `2px solid ${color}`,
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "bold",
                      color,
                      marginBottom: "0.3rem",
                    }}
                  >
                    {item.especialidad}
                  </div>
                  <div style={{ fontSize: "20px", fontWeight: "bold" }}>
                    {item.total}
                  </div>
                  <div style={{ fontSize: "12px", color: "#999" }}>chasis</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Resumen de SIM Activa */}
      {simActivaSummary && (
        <div
          style={{
            marginTop: "1rem",
            background: "#2a2a2a",
            color: "#ccc",
            padding: "1rem",
            borderRadius: "8px",
            border: "1px solid #333",
            textAlign: "center",
          }}
        >
          <p style={{ margin: 0, fontSize: "20px", fontWeight: "bold" }}>
            Vehículos Activados: {simActivaSummary.si}
          </p>
        </div>
      )}
    </div>
  );
};

export default MapConcentrationDashboard;
